import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  PlaneLanding, PlaneTakeoff, Ship, Anchor, Plus,
  Boxes, BarChart3, ChevronRight, ChevronLeft
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import './UserSidebar.css';

// The shipment lists all live on /user and are chosen by tab, so a nav entry
// is a (path, tab) pair rather than a route of its own.
// Export is what this account works in day to day, so it leads.
const NAV = [
  { id: 'Air-Export', label: 'Air Export', icon: PlaneTakeoff, children: [
    { id: 'shipments', label: 'Shipments', icon: Boxes, tab: 'Air-Export' },
    { id: 'analytics', label: 'Analytics', icon: BarChart3, to: '/user/insights', state: { transportTab: 'air' } },
  ] },
  { id: 'Sea-Export', label: 'Sea Export', icon: Anchor, children: [
    { id: 'shipments', label: 'Shipments', icon: Boxes, tab: 'Sea-Export' },
    { id: 'analytics', label: 'Analytics', icon: BarChart3, to: '/user/insights', state: { transportTab: 'sea' } },
  ] },
  { id: 'Air-Import', label: 'Air Import', icon: PlaneLanding, children: [
    { id: 'shipments', label: 'Shipments', icon: Boxes, tab: 'Air-Import' },
  ] },
  { id: 'Sea-Import', label: 'Sea Import', icon: Ship, children: [
    { id: 'shipments', label: 'Shipments', icon: Boxes, tab: 'Sea-Import' },
  ] },
];

const UserSidebar = ({ activeTab, onTabChange, collapsed, onToggleCollapse, mobileOpen, onCloseMobile }) => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Only the section holding the current tab is open, until someone opens another.
  const [openId, setOpenId] = useState(activeTab);
  useEffect(() => { if (activeTab) setOpenId(activeTab); }, [activeTab]);

  const art = isDarkMode
    ? '/images/theme-assets/sidebar-dark.png'
    : '/images/theme-assets/sidebar-light.png';

  const analyticsRoute = location.pathname.includes('insights');
  const onShipments = !analyticsRoute && !location.pathname.includes('new-booking');
  const bookingActive = location.pathname === '/user/new-booking';

  const go = (child) => {
    if (child.to) navigate(child.to, child.state ? { state: child.state } : undefined);
    else {
      if (location.pathname !== '/user') navigate('/user');
      onTabChange?.(child.tab);
    }
    onCloseMobile?.();
  };

  const cls = useMemo(() => [
    'ql-side',
    collapsed ? 'is-collapsed' : '',
    mobileOpen ? 'is-open' : '',
  ].filter(Boolean).join(' '), [collapsed, mobileOpen]);

  return (
    <>
      {mobileOpen && <button type="button" className="ql-side__scrim lg:hidden" aria-label="Close menu" onClick={onCloseMobile} />}

      <aside className={cls}>
        <div className="ql-side__art" style={{ backgroundImage: `url(${art})` }} aria-hidden="true" />

        <button
          type="button"
          className="ql-side__toggle"
          onClick={onToggleCollapse}
          title={collapsed ? 'Expand menu' : 'Collapse menu'}
          aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
        >
          {collapsed ? <ChevronRight size={15} /> : <ChevronLeft size={15} />}
        </button>

        <div className="ql-side__body">
          <div className="ql-side__brand">
            <img src="/images/logo.png" alt="QuickLook" />
          </div>

          <nav className="ql-side__nav">
            {NAV.map((group) => {
              const Icon = group.icon;
              const isCurrent = onShipments && activeTab === group.id;
              const open = !collapsed && openId === group.id;
              return (
                <div key={group.id}>
                  <button
                    type="button"
                    className={`ql-side__item${isCurrent ? ' is-active' : ''}`}
                    onClick={() => {
                      if (collapsed) { go(group.children[0]); return; }
                      setOpenId((prev) => (prev === group.id ? null : group.id));
                    }}
                    title={collapsed ? group.label : undefined}
                  >
                    <span className="ql-side__icon"><Icon size={19} /></span>
                    <span className="ql-side__label">{group.label}</span>
                    <ChevronRight size={15} className={`ql-side__chev${open ? ' is-open' : ''}`} />
                  </button>

                  {open && (
                    <div className="ql-side__sub">
                      {group.children.map((child) => {
                        const ChildIcon = child.icon;
                        const childActive = child.to
                          ? analyticsRoute
                          : (onShipments && activeTab === child.tab);
                        return (
                          <button
                            key={child.id}
                            type="button"
                            className={`ql-side__subitem${childActive ? ' is-active' : ''}`}
                            onClick={() => go(child)}
                          >
                            <ChildIcon size={16} />
                            <span>{child.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            <button
              type="button"
              className={`ql-side__item ql-side__cta${bookingActive ? ' is-active' : ''}`}
              onClick={() => { navigate('/user/new-booking'); onCloseMobile?.(); }}
              title={collapsed ? 'New Booking' : undefined}
            >
              <span className="ql-side__icon"><Plus size={17} /></span>
              <span className="ql-side__label">New Booking</span>
            </button>
          </nav>
        </div>
      </aside>
    </>
  );
};

export default UserSidebar;
